<template>
	<div class="pa-3 full-height new-back billing-report-page">
		<div class="billing-report-filters d-inline-flex flex-wrap align-center gap-3">
			<q-date-picker
				v-model="filters.startDate"
				@input="onStartDateChange"
				label="Start Date" type="month"
				outlined hide-details inner dense
			/>
			<q-date-picker
				v-model="filters.endDate"
				@input="onEndDateChange"
				label="End Date" type="month"
				outlined hide-details inner dense
			/>
			<v-autocomplete
				v-model="filters.billables"
				@change="getReport"
				:items="billables"
				label="Billable" item-value="id" item-text="code"
				class="billing-filter-field"
				:menu-props="{ contentClass: 'billing-report-filter-menu', zIndex: 300, attach: false }"
				outlined hide-details dense multiple
			/>
			<v-autocomplete
				v-model="filters.billableTypes"
				@change="getReport"
				:items="billableTypes"
				label="Billable Type" item-value="id" item-text="code"
				class="billing-filter-field"
				:menu-props="{ contentClass: 'billing-report-filter-menu', zIndex: 300, attach: false }"
				outlined hide-details dense multiple
			/>
			<q-user-autocomplete
				v-model="filters.users"
				@input="getReport"
				:items="users"
				label="User"
				class="billing-filter-field"
				outlined hide-details dense multiple
			/>
			<v-autocomplete
				v-model="filters.customers"
				@change="getReport"
				:items="customers"
				label="Customer" item-value="id" item-text="name"
				class="billing-filter-field"
				outlined hide-details dense multiple
			/>
			<v-autocomplete
				v-model="filters.byUser"
				@change="onByUserChange"
				:items="reportViews"
				item-value="value"
				item-text="text"
				label="View"
				class="billing-filter-field"
				outlined hide-details dense
			/>
			<v-autocomplete
				v-model="selectedSections"
				@change="onSectionsChange"
				:items="sectionOptions"
				item-value="value"
				item-text="text"
				label="Sections"
				class="billing-filter-field billing-filter-sections"
				outlined hide-details dense multiple
			>
				<template #selection="{ index }">
					<span
						v-if="index === 0"
						class="sections-selection-text"
						:title="sectionsSelectionText"
					>{{ sectionsSelectionText }}</span>
				</template>
			</v-autocomplete>
			<v-checkbox
				v-if="!filters.byUser"
				v-model="filters.includeWithoutBillable"
				@change="getReport"
				label="Without Billable"
				color="primary"
				class="mt-0 pt-0"
				style="height: 24px"
				hide-details
			/>
			<v-checkbox
				v-model="filters.includeBillablesOnZero"
				@change="getReport"
				label="Billables on Zero"
				color="primary"
				class="mt-0 pt-0"
				style="height: 24px"
				hide-details
			/>
		</div>

		<div class="chart new-back mt-2 bordered rounded-lg lighten" ref="chart">
			<div
				v-if="dates.length"
				class="pin-lines-overlay"
				:style="pinLinesOverlayStyle"
				aria-hidden="true"
			>
				<div
					class="pin-lines-pane pin-lines-frozen"
					:style="{ width: frozenWidth + 'px' }"
				>
					<div class="pin-lines-hgrid">
						<div
							v-for="(left, idx) in frozenPinVLinePositions"
							:key="'fh-' + idx"
							class="pin-line-h"
							:style="{
								top: 0,
								height: billingHeadHeightPx + 'px',
								left: (left - 1) + 'px',
							}"
						/>
					</div>
					<div class="pin-lines-vgrid">
						<div
							v-for="(left, idx) in frozenPinVLinePositions"
							:key="'fv-' + idx"
							class="pin-line-v"
							:style="{ left: left + 'px' }"
						/>
					</div>
				</div>
				<div
					v-if="fixTotals"
					class="pin-lines-pane pin-lines-totals"
					:style="{
						left: frozenWidth + 'px',
						width: visibleColumns.length * COL_TOTAL + 'px',
					}"
				>
					<div class="pin-lines-hgrid">
						<div
							v-for="(seg, idx) in totalsPinHeadSegmentLines"
							:key="'th-' + idx"
							class="pin-line-h"
							:style="{
								top: seg.top + 'px',
								height: seg.height + 'px',
								left: (seg.left - 1) + 'px',
							}"
						/>
					</div>
					<div class="pin-lines-vgrid">
						<div
							v-for="(lineLeft, idx) in totalsPinVLines"
							:key="'tv-' + idx"
							class="pin-line-v"
							:style="{ left: lineLeft + 'px' }"
						/>
					</div>
				</div>
			</div>
			<table
				:class="{ 'fix-totals': fixTotals, 'by-user': filters.byUser }"
				:style="{ '--billing-head-h': billingHeadHeightPx + 'px' }"
			>
				<colgroup>
					<col v-if="!filters.byUser" class="col-frozen-billable">
					<col v-if="!filters.byUser" class="col-frozen-type">
					<col class="col-frozen-bluetag">
					<template v-for="(date, pix) in dates">
						<col
							v-for="col in visibleColumns"
							:key="'col-' + pix + '-' + col.key"
							class="col-metric"
						>
					</template>
				</colgroup>
				<thead class="billing-report-head not-hover">
					<tr>
						<th v-if="!filters.byUser" :rowspan="headerRowSpan" class="billing-header-cell frozen-col frozen-col-billable">
							<div>
								Billable
							</div>
						</th>
						<th v-if="!filters.byUser" :rowspan="headerRowSpan" class="billing-header-cell frozen-col frozen-col-type">
							<div class="fill-w">
								Type
							</div>
						</th>
						<th :rowspan="headerRowSpan" class="billing-header-cell frozen-col frozen-col-bluetag label-column-end">
							<div>
								BlueTag
							</div>
						</th>
						<th
							v-for="(date, ix) in dates" :key="ix"
							:colspan="colsPerPeriod" scope="colgroup"
							class="billing-header-cell period-end-border" :class="{'fixed-total-sticky': date.isTotal && fixTotals}"
							:style="date.isTotal && fixTotals ? fixedTotalStyle(0) : null"
						>
							<div v-if="date.isTotal" class="relative">
								Totals
								<v-btn @click="fixTotals = !fixTotals" style="position: absolute; right: 10px" x-small icon>
									<v-icon>{{ fixTotals ? 'mdi-pin-off-outline' : 'mdi-pin-outline' }}</v-icon>
								</v-btn>
							</div>
							<span v-else>
								{{ date.format('MMM YYYY') }}
							</span>
						</th>
					</tr>
					<tr class="not-hover">
						<template v-for="(date, ix) in dates">
							<th
								v-for="group in sectionHeaderGroups"
								:key="ix + '-' + group.section"
								scope="col"
								:colspan="group.colspan"
								:rowspan="group.rowspan"
								:class="[sectionGroupClass(group), group.isLastGroup ? 'period-end-border' : '', date.isTotal && fixTotals ? 'fixed-total-sticky' : '']"
								:style="date.isTotal && fixTotals ? fixedTotalStyle(group.startColIndex) : null"
							>
								{{ group.text }}
							</th>
						</template>
					</tr>
					<tr v-if="visibleSubColumns.length" class="not-hover">
						<template v-for="(date, ix) in dates">
							<th
								v-for="col in visibleSubColumns"
								:key="ix + col.key"
								scope="col"
								:class="[columnHeaderClass(col, col.colIndex, ix), col.isLast ? 'period-end-border' : '', date.isTotal && fixTotals ? 'fixed-total-sticky' : '']"
								:style="date.isTotal && fixTotals ? fixedTotalStyle(col.colIndex) : null"
							>
								{{ col.label }}
							</th>
						</template>
					</tr>
				</thead>
				<tbody>
					<template v-for="line in shownLines">
						<tr
							@click="doHighlight(line.key)"
							:key="line.key + 'a'"
							class="sticky-header customer-header"
							:class="{ 'highlighted': line.key == selectedId }"
						>
							<th v-if="!filters.byUser" class="grid-data-cell frozen-col frozen-col-billable">
							<div class="nobr text-center col-billable">{{ line.billableCode }}</div>
						</th>
						<th v-if="!filters.byUser" class="grid-data-cell frozen-col frozen-col-type">
							<div class="text-center col-type">
								{{ billableTypeCode(line) }}
							</div>
						</th>
						<th class="grid-data-cell frozen-col frozen-col-bluetag grid-data-label-end">
							<div class="text-center col-bluetag">{{ line.userBlueTag }}</div>
						</th>
	
							<template v-for="(period, ix) in line.periods">
								<td
									v-for="(col, cix) in visibleColumns"
									:key="ix + col.key"
									class="quantity-cell-customer grid-data-cell"
									:class="[
										col.isLast ? 'period-end-border' : '',
										period.isTotal && fixTotals ? 'fixed-total-sticky' : '',
										col.unclassified && unclassifiedHit(line, period, col) ? 'unclassified-hit' : '',
									]"
									:style="period.isTotal && fixTotals ? fixedTotalStyle(cix) : null"
								>
									<template v-if="col.unclassified && showUnclassifiedTooltip(period)">
										<v-tooltip bottom content-class="unclassified-tooltip-popup" :z-index="200">
											<template v-slot:activator="{ on, attrs }">
												<div
													v-bind="attrs"
													v-on="on"
													:class="hoursClass(periodHours(period, col.field))"
												>{{ periodHours(period, col.field) | number(2, 0, true) }}</div>
											</template>
											<div class="unclassified-tooltip">
												<template v-for="(item, bix) in rpehUnclassifiedBreakdown(period)">
													<span
														:key="'l' + bix"
														class="unclassified-tooltip-label"
													>
														<b>{{ item.billableCode }}</b> ({{ typeCode(item.billableType) }})
													</span>
													<span :key="'s' + bix" class="unclassified-tooltip-sep">—</span>
													<span :key="'h' + bix" class="unclassified-tooltip-hours">{{ item.hours | number(2, 0, true) }}</span>
												</template>
											</div>
										</v-tooltip>
									</template>
									<div v-else :class="hoursClass(periodHours(period, col.field))">
										{{ periodHours(period, col.field) | number(2, 0, true) }}
									</div>
								</td>
							</template>
						</tr>
					</template>
					<template v-for="line in billableTypeTotals">
						<tr
							@click="doHighlight(line.key)"
							:key="line.key + 'a'"
							class="sticky-header customer-header billable-totals"
							:class="{ 'highlighted': line.key == selectedId }"
						>
							<th v-if="!filters.byUser" class="grid-data-cell frozen-col frozen-col-billable">
							<div class="nobr text-center col-billable"></div>
						</th>
						<th v-if="!filters.byUser" class="grid-data-cell frozen-col frozen-col-type">
							<div class="text-center col-type"></div>
						</th>
						<th class="grid-data-cell frozen-col frozen-col-bluetag grid-data-label-end">
							<div class="text-center col-bluetag">{{ filters.byUser ? byUserTotalsBluetagLabel : billableTypeCode(line) }}</div>
						</th>
	
							<template v-for="(period, ix) in line.periods">
								<td
									v-for="(col, cix) in visibleColumns"
									:key="ix + col.key"
									class="quantity-cell-customer grid-data-cell"
									:class="[
										col.isLast ? 'period-end-border' : '',
										period.isTotal && fixTotals ? 'fixed-total-sticky' : '',
										col.unclassified && unclassifiedHit(line, period, col) ? 'unclassified-hit' : '',
									]"
									:style="period.isTotal && fixTotals ? fixedTotalStyle(cix) : null"
								>
									<template v-if="col.unclassified && showUnclassifiedTooltip(period)">
										<v-tooltip bottom content-class="unclassified-tooltip-popup" :z-index="200">
											<template v-slot:activator="{ on, attrs }">
												<div
													v-bind="attrs"
													v-on="on"
													:class="hoursClass(periodHours(period, col.field))"
												>{{ periodHours(period, col.field) | number(2, 0, true) }}</div>
											</template>
											<div class="unclassified-tooltip">
												<template v-for="(item, bix) in rpehUnclassifiedBreakdown(period)">
													<span
														:key="'l' + bix"
														class="unclassified-tooltip-label"
													>
														<b>{{ item.billableCode }}</b> ({{ typeCode(item.billableType) }})
													</span>
													<span :key="'s' + bix" class="unclassified-tooltip-sep">—</span>
													<span :key="'h' + bix" class="unclassified-tooltip-hours">{{ item.hours | number(2, 0, true) }}</span>
												</template>
											</div>
										</v-tooltip>
									</template>
									<div v-else :class="hoursClass(periodHours(period, col.field))">
										{{ periodHours(period, col.field) | number(2, 0, true) }}
									</div>
								</td>
							</template>
						</tr>
					</template>
				</tbody>
			</table>
			<div class="pagination-container d-flex align-center">
				<div class="d-flex justify-end py-2 ml-auto">
					<div class="d-flex align-center mr-5 gap-1 nobr">
						Page: <b>{{ pagination.page }} / {{ pagination.pages }}</b>
					</div>
					<div class="d-flex align-center mr-5 gap-1 nobr">
						Rows per page:
						<v-select v-model="pagination.pageSize" :items="[5, 10, 20, 'All']" dense hide-details
							style="width: 50px" class="caption" />
					</div>
					<div style="position: relative">
						<v-pagination
							v-model="pagination.page"
							:length="pagination.pages"
							:total-visible="0"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment"

const COL_BILLABLE = 112
const COL_TYPE = 42
const COL_BLUETAG = 96
const COL_TOTAL = 70

const COLUMN_TINT_FIELDS = new Set([
	'assignedBillableHours',
	'executedBillableHours',
	'executedUnclassifiedHours',
])
const COLUMN_DEFS = [
	{ key: 'a', field: 'assignedBillableHours', section: 'assigned', label: 'Billable' },
	{ key: 'b', field: 'assignedNonBillableHours', section: 'assigned', label: 'Non Billable' },
	{ key: 'c', field: 'executedBillableHours', section: 'executed', label: 'Billable' },
	{ key: 'd', field: 'executedNonBillableHours', section: 'executed', label: 'Non Billable' },
	{ key: 'e', field: 'executedUnclassifiedHours', section: 'executed', label: 'Unclassified', unclassified: true },
	{ key: 'f', field: 'billedHours', section: 'billed', label: 'Billed' },
]

const SECTION_ORDER = ['assigned', 'executed', 'billed']
const BILLABLE_TYPE_CODES = ['FxR', 'RPAH', 'RPEH', 'Int']
const RPEH_BILLABLE_TYPE = 2

export default {
	title: 'Billing Report',
	data: () => ({
		selectedId: null,
		report: [],
		billableTypeTotals: [],
		filters: {
			startDate: moment().startOf('year').format('YYYY-MM-DD'),
			endDate: moment().endOf('year').format('YYYY-MM-DD'),
			billables: [],
			billableTypes: [],
			users: [],
			customers: [],
			includeWithoutBillable: false,
			includeBillablesOnZero: false,
			byUser: false,
		},
		pagination: {
			page: 1,
			pageSize: 20,
			pages: 1,
		},
		billables: [],
		billableTypes: [],
		users: [],
		customers: [],
		reportViews: [
			{ value: false, text: 'By Billable' },
			{ value: true, text: 'By User' },
		],
		sectionOptions: [
			{ value: 'assigned', text: 'Assigned' },
			{ value: 'executed', text: 'Executed' },
			{ value: 'billed', text: 'Billed' },
		],
		selectedSections: ['assigned', 'executed', 'billed'],

		fixTotals: false,
		pinOverlayHeight: 0,
	}),
	created() {
		this.$q.log(4, 54, 3, 'NA')

		this.$http.get('api/enums/admin/billable/type')
			.then(res => {
				this.billableTypes = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch billable types')
			})
		this.$http.get('api/billable')
			.then(res => {
				this.billables = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch billables')
			})
		this.$http.get('api/user?onlyInterns=true')
			.then(res => {
				this.users = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch users')
			})
		this.$http.get('api/customer')
			.then(res => {
				this.customers = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch customers')
			})

		this.getReport()
	},
	computed: {
		shownLines() {
			let size = this.pagination.pageSize

			if(size == 'All') size = this.report.length

			let start = (this.pagination.page - 1) * size
			let end = start + size
			return this.report.slice(start, end)
		},
		dates() {
			if (!this.report.length) return []

			return this.report[0].periods
				.map(x => {
					let m = moment(x.period)
					m.isTotal = x.isTotal
					return m
				})
		},
		visibleSections() {
			return SECTION_ORDER.filter(section => this.selectedSections.includes(section))
		},
		visibleColumns() {
			const cols = COLUMN_DEFS.filter(col =>
				col.field === 'executedUnclassifiedHours' || this.selectedSections.includes(col.section)
			)
			return cols.map((col, index) => {
				const sectionColCount = cols.filter(c => c.section === col.section).length
				return {
					...col,
					colIndex: index,
					isLast: index === cols.length - 1,
					isFirstInSection: index === 0 || cols[index - 1].section !== col.section,
					isLastInSection: index === cols.length - 1 || cols[index + 1].section !== col.section,
					mergedWithSection: sectionColCount === 1,
				}
			})
		},
		visibleSubColumns() {
			return this.visibleColumns.filter(col => !col.mergedWithSection)
		},
		colsPerPeriod() {
			return this.visibleColumns.length
		},
		headerRowSpan() {
			return this.visibleSubColumns.length ? 3 : 2
		},
		sectionHeaderGroups() {
			let startColIndex = 0
			const groups = []

			this.visibleSections.forEach((section, index) => {
				const columns = this.visibleColumns.filter(col => col.section === section)
				if (!columns.length) return

				groups.push({
					section,
					text: this.sectionOptions.find(x => x.value === section).text,
					colspan: columns.length,
					rowspan: columns.length === 1 ? 2 : 1,
					startColIndex,
					isLastGroup: false,
				})
				startColIndex += columns.length
			})

			const unclassifiedCols = this.visibleColumns.filter(col => col.field === 'executedUnclassifiedHours')
			if (unclassifiedCols.length && !this.selectedSections.includes('executed')) {
				groups.push({
					section: 'unclassified',
					text: 'Unclassified',
					colspan: unclassifiedCols.length,
					rowspan: 2,
					startColIndex,
					isLastGroup: true,
				})
			}
			else if (groups.length) {
				groups[groups.length - 1].isLastGroup = true
			}

			return groups
		},
		frozenWidth() {
			return this.filters.byUser
				? COL_BLUETAG
				: COL_BILLABLE + COL_TYPE + COL_BLUETAG
		},
		sectionsSelectionText() {
			if (SECTION_ORDER.every(section => this.selectedSections.includes(section)))
				return 'All'

			return SECTION_ORDER
				.filter(section => this.selectedSections.includes(section))
				.map(section => this.sectionOptions.find(x => x.value === section).text)
				.join(', ')
		},
		byUserTotalsBluetagLabel() {
			return `Total (${this.report.length})`
		},
		billingHeadHeightPx() {
			const rows = this.visibleSubColumns.length ? 3 : 2
			return rows * 24
		},
		pinLinesOverlayStyle() {
			const h = this.pinOverlayHeight
			return {
				height: `${h}px`,
				marginBottom: `${-h}px`,
				'--billing-head-h': `${this.billingHeadHeightPx}px`,
			}
		},
		frozenPinVLinePositions() {
			// Solo el borde exterior del bloque fijo (sin verticales entre Billable | Type | BlueTag)
			return [this.frozenWidth]
		},
		totalsPinVLines() {
			const n = this.visibleColumns.length
			const lines = []
			for (let i = 1; i <= n; i++)
				lines.push(i * COL_TOTAL)
			return lines
		},
		totalsPinHeadSegmentLines() {
			const rowH = 24
			const n = this.visibleColumns.length
			const lines = []
			const push = (top, height, colEndIndex) => {
				lines.push({
					top,
					height,
					left: colEndIndex * COL_TOTAL,
				})
			}

			push(0, rowH, n)

			if (this.visibleSubColumns.length) {
				this.sectionHeaderGroups.forEach(group => {
					push(rowH, rowH, group.startColIndex + group.colspan)
				})
				for (let i = 1; i <= n; i++)
					push(rowH * 2, rowH, i)
			}
			else {
				this.sectionHeaderGroups.forEach(group => {
					push(rowH, group.rowspan > 1 ? rowH * 2 : rowH, group.startColIndex + group.colspan)
				})
			}

			return lines
		},
	},
	methods: {
		onStartDateChange() {
			if (this.filters.startDate)
				this.filters.startDate = moment(this.filters.startDate).startOf('month').format('YYYY-MM-DD')
			this.getReport()
		},
		onEndDateChange() {
			if (this.filters.endDate)
				this.filters.endDate = moment(this.filters.endDate).endOf('month').format('YYYY-MM-DD')
			this.getReport()
		},
		getReport() {
			this.$http.post('api/admin/billingReport', this.filters)
				.then(res => {
					this.report = res.data.lines
					this.billableTypeTotals = res.data.billableTypeTotals

					if(this.pagination.pageSize == 'All')
						this.pagination.pages = 1
					else
						this.pagination.pages = Math.ceil(this.report.length / this.pagination.pageSize)
					this.pagination.page = 1
					this.updatePinOverlayHeight()
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Error getting report')
				})
		},
		doHighlight(id){
			if(id == this.selectedId)
				this.selectedId = null
			else
				this.selectedId = id
		},
		onByUserChange(){
			if(this.filters.byUser)
				this.filters.includeWithoutBillable = false
			this.pagination.pageSize = this.filters.byUser ? 'All' : 20
			this.getReport()
		},
		onSectionsChange(){
			if(!this.selectedSections.length)
				this.selectedSections = SECTION_ORDER.slice()
		},
		billableTypeCode(line){
			const type = line?.billableType
			if (type === null || type === undefined) return '-'
			return BILLABLE_TYPE_CODES[Number(type)] || '-'
		},
		isRpehLine(line){
			return Number(line?.billableType) === RPEH_BILLABLE_TYPE
		},
		periodHours(period, field){
			const value = period?.[field]
			if (value === null || value === undefined || value === '') return 0
			const hours = Number(value)
			return Number.isFinite(hours) ? hours : 0
		},
		typeCode(type){
			return BILLABLE_TYPE_CODES[Number(type)] || '-'
		},
		rpehUnclassifiedBreakdown(period){
			return (period?.rpehUnclassifiedBreakdown || []).filter(x => Number(x.hours) > 0)
		},
		showUnclassifiedTooltip(period){
			return this.filters.byUser && this.rpehUnclassifiedBreakdown(period).length > 0
		},
		unclassifiedHit(line, period, col){
			if (!col.unclassified) return false
			if (this.periodHours(period, col.field) <= 0) return false
			if (this.isRpehLine(line)) return true
			if (this.filters.byUser && this.rpehUnclassifiedBreakdown(period).length) return true
			return false
		},
		fixedTotalStyle(colIndex){
			const left = this.frozenWidth + COL_TOTAL * colIndex
			return { left: `${left}px` }
		},
		sectionGroupClass(group){
			const classes = ['billing-header-cell']
			if(group.rowspan > 1) classes.push('section-merged')
			if(group.section === 'assigned') classes.push('subheader-assigned')
			else if(group.section === 'executed') classes.push('subheader-executed')
			else if(group.section === 'billed') classes.push('subheader-billed')
			return classes
		},
		columnHeaderClass(col, colIndex, periodIndex){
			const classes = ['billing-header-cell']
			if (COLUMN_TINT_FIELDS.has(col.field)) classes.push('header-subcol-tint')
			else if (col.section === 'assigned' || col.section === 'executed') classes.push('header-subcol-plain')
			if (col.isFirstInSection && col.section !== 'assigned') classes.push('header-section-l')
			return classes
		},
		hoursClass(value){
			const intDigits = Math.floor(Math.abs(Number(value) || 0)).toString().length
			if(intDigits > 6) return 'cell-xxs'
			if(intDigits > 4) return 'cell-xs'
			return ''
		},
		updatePinOverlayHeight(){
			this.$nextTick(() => {
				const table = this.$refs.chart?.querySelector('table')
				this.pinOverlayHeight = table?.offsetHeight ?? 0
			})
		},
	},
	watch: {
		fixTotals(){
			this.updatePinOverlayHeight()
		},
		report: 'updatePinOverlayHeight',
		selectedSections: 'updatePinOverlayHeight',
		'pagination.page'(v){
			this.updatePinOverlayHeight()
		},
		'pagination.pageSize'(v){
			if(v == 'All'){
				this.pagination.pages = 1
			}
			else {
				this.pagination.pages = Math.ceil(this.report.length / this.pagination.pageSize)
			}
			this.updatePinOverlayHeight()
		}
	},
	mounted(){
		this.updatePinOverlayHeight()
	},
	updated(){
		if (this.dates.length)
			this.updatePinOverlayHeight()
	},
}
</script>

<style lang="scss" scoped>
$col-billable: 112px;
$col-type: 42px;
$col-bluetag: 96px;
$col-total: 70px;
$col-seam: 1px;
$bg-assigned: #fdebd8;
$bg-executed: #f0eafa;
$bg-billed: #e3f2e8;
$billing-grid-border: 1px solid rgba(0, 0, 0, 0.12);

.col-billable { width: 100%; max-width: $col-billable; font-weight: 400; }
.col-type { width: 100%; max-width: $col-type; font-weight: 400; }
.col-bluetag { width: 100%; max-width: $col-bluetag; font-weight: 400; }

.subheader-assigned { background: $bg-assigned !important; }
.subheader-executed { background: $bg-executed !important; }
.subheader-billed { background: $bg-billed !important; }

$billing-head-row-h: 24px;

// Header in thead — single sticky block (no per-row sticky gaps)
thead.billing-report-head {
	position: sticky;
	top: 0;
	z-index: 5;
	background: var(--v-newDesignBackground-base);
	// border-collapse + sticky drops cell border-bottom; paint the header/body seam here
	box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.12);

	tr {
		position: static;
		&::after {
			display: none !important;
		}
	}

	th.billing-header-cell {
		height: $billing-head-row-h;
		line-height: $billing-head-row-h;
		padding: 0;
		margin: 0;
		box-sizing: border-box;
		vertical-align: middle;
		border-top: none;
		border-left: none;
		border-bottom: none;
		width: $col-total;
		&::after,
		&::before {
			display: none !important;
			content: none !important;
		}
	}

	th.billing-header-cell.frozen-col-billable {
		width: $col-billable;
		min-width: $col-billable;
		max-width: $col-billable;
	}

	th.billing-header-cell.frozen-col-type {
		width: $col-type;
		min-width: $col-type;
		max-width: $col-type;
	}

	th.billing-header-cell.frozen-col-bluetag {
		width: $col-bluetag;
		min-width: $col-bluetag;
		max-width: $col-bluetag;
	}

	th.header-subcol-tint {
		background: var(--v-coloredBack-base) !important;
	}
	th.header-subcol-plain {
		background: var(--v-newDesignBackground-base);
	}

	th.section-merged {
		vertical-align: middle;
	}

	th.section-merged::after {
		display: none !important;
	}

	tr:last-child th.billing-header-cell {
		// Seam comes from thead box-shadow (sticky + collapse drops this border)
		border-bottom: none !important;
	}

	th.period-end-border {
		border-right: $billing-grid-border !important;
	}

	th.label-column-end {
		border-right: none !important;
	}

	.frozen-col {
		position: sticky;
		box-sizing: border-box;
		background: var(--v-newDesignBackground-base) !important;

		&::after {
			display: none !important;
		}
	}

	.frozen-col-billable {
		z-index: 9;
	}

	.frozen-col-type {
		z-index: 10;
	}

	.frozen-col-bluetag {
		z-index: 11;
	}
}

.unclassified-hit { background: #fbe3e3 !important; }

.unclassified-tooltip {
	display: grid;
	grid-template-columns: max-content auto max-content;
	column-gap: 0.35em;
	row-gap: 4px;
	line-height: 1.5;
	padding: 0px;
	align-items: baseline;
	width: max-content;

	.unclassified-tooltip-label {
		text-align: left;
		white-space: nowrap;
	}

	.unclassified-tooltip-sep {
	}

	.unclassified-tooltip-hours {
		text-align: right;
		font-variant-numeric: tabular-nums;
		white-space: nowrap;
	}
}

.highlighted {
	background-color: #c4dcec !important;
	td, th {
		background-color: #c4dcec !important;
	}
	.frozen-col {
		background-color: #c4dcec !important;
	}
}

.frozen-col-billable {
	left: 0;
	z-index: 4;
}

.frozen-col-type {
	// Overlap 1px: sticky `left` vs table column edge leaves a hairline when scrolling
	left: calc(#{$col-billable} - 1px);
	z-index: 5;
}

.frozen-col-bluetag {
	// -2px: type column is already shifted -1px; cover shared border before BlueTag
	left: calc(#{$col-billable} + #{$col-type} - 2px);
	z-index: 6;
}

table.by-user {
	.frozen-col-bluetag {
		left: 0;
	}
}

.fixed-total-sticky {
	position: sticky;
	z-index: 7;
	box-sizing: border-box;
	background: var(--v-newDesignBackground-base);
}

table.fix-totals {
	thead th.fixed-total-sticky,
	tbody td.fixed-total-sticky {
		border-right: none !important;
		box-shadow: none !important;

		&::after {
			display: none !important;
		}
	}
}

.chart table {
	thead th.frozen-col,
	thead th.billing-header-cell.frozen-col,
	tbody th.frozen-col.grid-data-cell {
		border-right: none !important;
	}
}

.pin-lines-overlay {
	position: sticky;
	top: 0;
	left: 0;
	width: 0;
	z-index: 12;
	pointer-events: none;
	overflow: visible;
}

.pin-lines-pane {
	position: absolute;
	top: 0;
	height: 100%;
}

.pin-lines-frozen {
	left: 0;
	z-index: 1;
}

.pin-lines-totals {
	z-index: 2;
}

.pin-lines-hgrid {
	position: relative;
	height: var(--billing-head-h);
	width: 100%;
}

.pin-line-h {
	position: absolute;
	width: 1px;
	background: rgba(0, 0, 0, 0.12);
}

.pin-lines-vgrid {
	position: absolute;
	top: var(--billing-head-h);
	left: 0;
	right: 0;
	bottom: 0;
}

.pin-line-v {
	position: absolute;
	top: 0;
	bottom: 0;
	width: 1px;
	margin-left: -1px;
	background: rgba(0, 0, 0, 0.12);
}

.billing-report-page {
	.billing-report-filters {
		position: relative;
		z-index: 20;
	}
}

th.fixed-total-sticky.subheader-assigned {
	background: $bg-assigned !important;
}

th.fixed-total-sticky.subheader-executed {
	background: $bg-executed !important;
}

th.fixed-total-sticky.subheader-billed {
	background: $bg-billed !important;
}

th.fixed-total-sticky.header-subcol-tint {
	background: var(--v-coloredBack-base) !important;
}

td.fixed-total-sticky,
th.fixed-total-sticky:not([colspan]) {
	width: $col-total;
	min-width: $col-total;
	max-width: $col-total;
}

tbody .billable-totals {
	td.fixed-total-sticky {
		background: var(--v-background-lighten3);
	}
}

.chart table {
	border-collapse: collapse;
	border-spacing: 0;
}

.chart table col.col-frozen-billable {
	width: $col-billable;
}

.chart table col.col-frozen-type {
	width: $col-type;
}

.chart table col.col-frozen-bluetag {
	width: $col-bluetag;
}

.chart table col.col-metric {
	width: $col-total;
	min-width: $col-total;
}

.chart table th,
.chart table td {
	padding: 0;
	margin: 0;
	background: var(--v-newDesignBackground-base);
}

.chart table tr {
	background: var(--v-newDesignBackground-base);
}

tbody {
	.frozen-col {
		position: sticky;
		box-sizing: border-box;
		background: var(--v-newDesignBackground-base);
	}

	th.frozen-col-billable {
		width: $col-billable;
		min-width: $col-billable;
		max-width: $col-billable;
	}

	th.frozen-col-type {
		width: $col-type;
		min-width: $col-type;
		max-width: $col-type;
	}

	th.frozen-col-bluetag {
		width: $col-bluetag;
		min-width: $col-bluetag;
		max-width: $col-bluetag;
	}

	.grid-data-cell {
		box-sizing: border-box;
		border-bottom: $billing-grid-border !important;
		border-right: $billing-grid-border !important;
		border-top: none !important;
		border-left: none !important;
	}

	.period-end-border {
		border-right: $billing-grid-border !important;
	}
	.customer-header {
		position: sticky;
		top: var(--billing-head-h, 72px);
		z-index: 2;
	}

	.billable-totals {
		th.frozen-col,
		th,
		td {
			background: var(--v-background-lighten3);
		}
	}
}

tfoot {
	border: none;
}

tr:not(.highlighted, .not-hover):hover {
	background: var(--v-lightgray-lighten2);

	> th, > td {
		background: var(--v-lightgray-lighten2) !important;
	}
	> th.frozen-col, > td.frozen-col {
		background: var(--v-lightgray-lighten2) !important;
	}
}

tr > td.sticky,
tr > th.sticky {
	position: sticky !important;
	left: 0;
	z-index: 1;
	background: var(--v-newDesignBackground-base);
}

.quantity-cell-customer {
	text-align: center;
	height: 28px;
	padding: 0;
	margin: 0;

	> div {
		width: $col-total;
		height: 28px;
		line-height: 28px;
		font-size: 1.15em;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;

		&.cell-xs {
			font-size: 0.98em;
		}

		&.cell-xxs {
			font-size: 0.82em;
		}
	}
}

.chart::-webkit-scrollbar {
	height: 8px;
	background: var(--v-newDesignBackground-base);
	border-radius: 15px;
}

.chart::-webkit-scrollbar-thumb {
	background: var(--v-background-lighten2);
	border: 4px solid transparent;
	border-radius: 8px;

	&:hover {
		background: var(--v-coloredBack-base);
	}
}

.chart {
	max-height: calc(100vh - 20px);
	width: calc(100vw - 190px);
	overflow: scroll;
	font-size: .72em;
	position: relative;
}

.billing-filter-field {
	flex: 0 0 auto;
	min-width: 200px;
	max-width: 280px;
}

.billing-filter-sections {
	::v-deep .v-select__selections {
		flex-wrap: nowrap;
		overflow: hidden;
	}

	.sections-selection-text {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 100%;
	}
}

.header {
	position: sticky;
	top: 0;
	z-index: 2;
}

.customer-header-cont {
	padding: 10px 10px;

	.customer-header {
		padding: 5px 5px 5px 10px !important;
	}
}

.line {
	display: flex;
	width: max-content;
	width: 100%;
	background: var(--v-newDesignBackground-base);

	&:hover {
		background: var(--v-lightgray-lighten2);
	}

	.sticky {
		display: flex;
		position: sticky;
		align-items: flex-end;
		background: inherit;
		left: 0;
		z-index: 1;
		border-right: 2px solid var(--v-lightgray-lighten);
		overflow: hidden;
		width: 325px;

		.st {
			display: flex;
			align-items: center;
			padding: 10px 5px 10px 10px;

			div {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}

		.head {
			border-bottom: 2px solid var(--v-lightgray-lighten);
		}
	}

	.date {
		width: 670px;
		padding-top: 5px;
		border: 1px solid var(--v-lightgray-lighten);
		background: var(--v-newDesignBackground-base);
		border-bottom-width: 2px;
		border-top: 0;

		&.no-border {
			border-left: none !important;
		}

		.head {
			font-size: 1.2em;
		}
	}

	.period-grid {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		width: 670px;

		::v-deep>* {
			padding-right: 5px !important;
		}
	}
}

.data-container {
	.customer-lines {
		border-bottom: 1px solid var(--v-lightgray-lighten);

		.line {
			contain: paint;
		}
	}
}

.pagination-container {
	position: sticky;
	left: 0px;
}

.non-billable{
	font-style: italic;
}

::v-deep .v-pagination__navigation {
	box-shadow: none !important;
	background: transparent !important;
}
</style>

<style lang="scss">
/* v-tooltip content is teleported to document body; overrides Vuetify inline opacity: 0.9 */
.v-tooltip__content.unclassified-tooltip-popup {
	width: max-content !important;
	max-width: 90vw !important;
	background-color: #616161 !important;
	opacity: 1 !important;
	color: #fff !important;
}

.v-menu__content.billing-report-filter-menu {
	z-index: 300 !important;
}
</style>